/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Fields } from "@cd2/forms"
import { useState } from "react"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { IRecord } from "../../types/schema"

type IRma = IRecord<"rmas", "identifier"> & {
  customer?: IRecord<"customers", "name">
  contact?: IRecord<"contacts", "name">
}

export function RmaSelect({
  value,
  onChange,
  label,
  disabled,
  branchId,
  customerId,
  isClearable,
  skeemFilter,
  error,
  includeNew,
}: {
  value?: string
  onChange(id: string, rma?: IRma): void
  label?: string
  disabled?: boolean
  isClearable?: boolean
  branchId?: string
  customerId?: string
  skeemFilter?: any
  error?: string
  includeNew?: boolean
}) {
  const skeem = window.skeem

  const [records, setRecords] = useState<IRma[]>([])

  function handleChange(id: string) {
    onChange(
      id,
      records.find(v => v.id === id),
    )
  }

  useAsyncEffect(
    async ({ isAlive }) => {
      const records = await skeem.fetch2<IRma[]>({
        model: {
          name: "rmas",
          attributes: [
            "identifier",
            { name: "customer", attributes: ["name"] },
            { name: "contact", attributes: ["name"] },
          ],
          filter: {
            and: [
              branchId && {
                gt: [
                  {
                    count: {
                      path: ["customer", { name: "branch", filter: { matches: { id: branchId } } }],
                    },
                  },
                  { value: 0 },
                ],
              },
              customerId && { matches: { customer: customerId } },
              skeemFilter,
            ].filter(Boolean),
          },
          sort: { by: "created_at", direction: "desc" },
        },
      })
      if (isAlive()) {
        let opts = [{ id: "NEW", identifier: "Create new RMA" }, ...records]
        if (!includeNew) {
          opts = opts.filter(x => x.id !== "NEW")
        }
        setRecords(opts)
      }
    },
    [branchId, customerId, value],
  )

  if (records.length === 0) {
    return <Fields.Select2 label={label ?? "rma"} disabled />
  }

  const opts = records?.map(record => ({ value: record.id, label: record.identifier }))
  const selected = opts.find(record => record.value === value)
  return (
    <Fields.Select2
      label={label ?? "rma"}
      options={records?.map(record => ({ value: record.id, label: record.identifier }))}
      onChange={opt => handleChange(opt?.value)}
      value={selected}
      disabled={disabled}
      isClearable={isClearable}
      error={error}
      components={{
        SingleValue: props => {
          const { innerProps, getStyles } = props
          const record = records.find(record => record.id === props.data.value)

          return (
            <div
              {...innerProps}
              style={{
                ...getStyles("singleValue", props),
              }}
            >
              <div>{record?.identifier}</div>
              <div
                css={css`
                  color: #333;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {record?.id.includes("NEW") ? null : record?.customer?.name ?? "No customer"}
              </div>
              {record?.contact && (
                <div
                  css={css`
                    color: #333;
                    font-size: 12px;
                    font-weight: 500;
                  `}
                >
                  {record?.contact.name}
                </div>
              )}
            </div>
          )
        },
        Option: props => {
          const { innerProps, innerRef, getStyles } = props
          const record = records.find(record => record.id === props.data.value)

          return (
            <div
              ref={innerRef}
              {...innerProps}
              style={{
                ...getStyles("option", props),
              }}
            >
              <div>{record?.identifier}</div>
              <div
                css={css`
                  color: #333;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {record?.id.includes("NEW") ? null : record?.customer?.name ?? "No customer"}
              </div>
              {record?.contact && (
                <div
                  css={css`
                    color: #333;
                    font-size: 12px;
                    font-weight: 500;
                  `}
                >
                  {record?.contact.name}
                </div>
              )}
            </div>
          )
        },
      }}
    />
  )
}
